<template>
  <v-card class="ReportCard mb-5" text>
      <v-card-title class="px-0 pl-12">
        {{equipData.name}}
        <v-tooltip right nudge-bottom="10">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" class="ml-2" :size="18" color="#757575">info_outline</v-icon>
          </template>
          <p class="mb-0 body-1">
            <span style="font-weight: 300; font-size: 14px !important;">{{$t("MainCard.brand")}}:</span> <span style="font-weight: 400; font-size: 14px !important;">{{equipData.brand || '-'}}</span>
          </p>
          <p class="mb-0 body-1">
            <span style="font-weight: 300; font-size: 14px !important;">{{$t("MainCard.flow")}}:</span> <span style="font-weight: 400; font-size: 14px !important;">{{equipData.pumpFlow || '-'}} m3/h</span>
          </p>
          <p class="mb-0 body-1">
            <span style="font-weight: 300; font-size: 14px !important;">{{$t("MainCard.area")}}:</span> <span style="font-weight: 400; font-size: 14px !important;">{{equipData.equipmentArea || '-'}} ha</span>
          </p>
        </v-tooltip>
      </v-card-title>
      <v-row row no-wrap class="EquipmentSummary px-12">
           <v-col xs3 class="EquipmentItem pa-0">
              <div
                v-for="(loc, index) in optLocations"
                :key="loc.id"
                :class="{LocationOpt:true, Active: loc.checked, 'd-flex':true, 'align-start': true, 'justify-start': true}"
                @click="toggleLoc(index)"
              >
                <v-icon v-if="loc.isPublished === false" color="warning" class="UnpublishedIcon">priority_high</v-icon>

                <v-icon v-if="loc.checked" small color="#0098E1" class="mt-1">radio_button_checked</v-icon>
                <v-icon v-else small class="mt-1">radio_button_unchecked</v-icon>
                <!-- <v-icon small :color="loc.checked ? '#0098E1' : '#757575'" class="mt-1">grass</v-icon> -->
                <CropIcon :color="loc.checked ? '#0098E1' : '#757575'" classString="" :name="loc.crop"></CropIcon>
                <p class="subheading d-inline-block ml-1">{{loc.label}}</p>
              </div>
           </v-col>

           <v-col
            v-for="(item, i) in metricsData"
            :key="metricsVisualData[i].label"
            xs3
            class="EquipmentItem text-center pa-0"
           >
            <p class="subheading px-1">{{metricsVisualData[i].label}}</p>

            <v-badge color="transparent">
              <template v-slot:badge>
                <v-icon
                  v-if="haveBadge('star', item.value, i)"
                  medium
                  color="#F8C300">star</v-icon>
                <v-icon
                  v-else-if="haveBadge('alert', item.value, i)"
                  medium
                  color="#F93A2F">report</v-icon>
              </template>

              <v-progress-circular
                v-if="metricsVisualData[i].circular"
                :rotate="-90"
                :size="120"
                :width="5"
                :value="item.value"
                color="#0098E1"
                class="ItemValue"
              >
                <p class="display-1 ma-0" style="color: #00202F">{{item.value.toFixed(0)}}</p>
                <p v-if="parseFloat(item.mod.value) === 0" class="caption ma-0 grey--text">
                  <v-icon size="12" color="grey">horizontal_rule</v-icon>
                  {{item.mod.value}}
                </p>
                <p
                  v-else
                  :class="{caption: true, 'ma-0': true, 'light-green--text': isModGood(item.mod, i), 'red--text': !isModGood(item.mod, i),'lighten-2': true}"
                >
                  <v-icon
                    v-if="item.mod.neg"
                    small
                    :color="isModGood(item.mod, i) ? 'light-green lighten-2' : 'red lighten-2'"
                  >arrow_downward</v-icon>
                  <v-icon
                    v-else
                    small
                    :color="isModGood(item.mod, i) ? 'light-green lighten-2' : 'red lighten-2'"
                  >arrow_upward</v-icon>
                  {{item.mod.value}}
                </p>
              </v-progress-circular>
              <div v-else class="my-3 py-3">
                <p class="display-1 ma-0 mb-1">{{item.value}}</p>

                <p v-if="parseFloat(item.mod.value) === 0" class="caption ma-0 grey--text">
                  <v-icon size="12" color="grey">horizontal_rule</v-icon>
                  {{item.mod.value}}
                </p>
                <p
                  v-else
                  :class="{caption: true, 'ma-0': true, 'light-green--text': isModGood(item.mod, i), 'red--text': !isModGood(item.mod, i),'lighten-2': true}"
                >
                  <v-icon
                    v-if="item.mod.neg"
                    small
                    :color="isModGood(item.mod, i) ? 'light-green lighten-2' : 'red lighten-2'"
                  >arrow_downward</v-icon>
                  <v-icon
                    v-else
                    small
                    :color="isModGood(item.mod, i) ? 'light-green lighten-2' : 'red lighten-2'"
                  >arrow_upward</v-icon>
                  {{item.mod.value}}
                </p>
              </div>
            </v-badge>
           </v-col>
      </v-row>
      <div v-if="faultsEvery100hsAverage && faultsEvery100hs > faultsEvery100hsAverage" class="py-2 px-12" style="background-color: #F3ECD8">
        <p class="mb-0" style="font-size: 14px">{{ $t('MainCard.auditMessage') }} <a style="color: #26A9E8" @click="requestAuditQuote"><strong>{{ $t('MainCard.quoteAudit') }}</strong></a></p>
      </div>
      <div class="pt-3">
        <p
          v-for="(comment, i) in equipmentComments"
          :key="equipData.name + 'equip_comment' + i"
          class="EquipmentComment subheading mx-5 my-2"
        >*{{comment}}</p>
      </div>
      <CardExpansionPanel
        :equipData="equipData"
        :locations="locations"
        :optLocations="optLocations"
        :title="equipData.name"
      />
  </v-card>
</template>

<script>
import CardExpansionPanel from './CardExpansionPanel'
import CropIcon from './CropIcon'
import { parsePhoneNumberForWaMe } from '../../lib/helpers'
import { trackEvent } from '../../lib/track-events'
import { EVENTS_TO_TRACK } from '../../constants/const'

export default {
  data () {
    return {
      optLocations: this.initOptLocations(),
      metricsData: this.setMetricsData(true),
      metricsVisualData: this.getMetricsVisualData()
    }
  },
  props: ['equipData', 'locations', 'badgesInfo', 'deviceId', 'lot', 'firstLoad', 'faultsEvery100hsAverage'],
  components: {
    CardExpansionPanel,
    CropIcon
  },
  watch: {
    locations () {
      this.optLocations = this.initOptLocations()
      this.metricsData = this.setMetricsData()
    }
  },
  methods: {
    initOptLocations () {
      // Get the lot object
      const searchedLot = this.locations.find((loc) => (this.deviceId === this.equipData.id && this.lot === loc.id))

      // If lot searched by url params does not exist, show an alert
      if (this.firstLoad && this.deviceId && this.deviceId === this.equipData.id && this.lot && !searchedLot) this.$parent.alertURLReportNotFound()

      return this.locations.map((loc, i) => ({
        checked: searchedLot && this.firstLoad ? this.lot === loc.id : i === 0,
        label: loc.name,
        id: loc.id,
        crop: loc.details.description.crop,
        isPublished: loc.isChecked
      }))
    },
    toggleLoc (index) {
      if (!this.optLocations[index].checked) {
        this.optLocations[index].checked = true
        this.metricsData = this.setMetricsData()
        trackEvent(EVENTS_TO_TRACK.CLICK_SELECT_LOT_REPORT, {locationId: this.optLocations[index].id, lotName: this.optLocations[index].label, deviceId: this.equipData.id, deviceName: this.equipData.name})
      } else {
        const locFinded = this.optLocations.findIndex((loc, i) => (i !== index && loc.checked))
        if (locFinded !== -1) {
          this.optLocations[index].checked = false
          this.metricsData = this.setMetricsData()
        }
        trackEvent(EVENTS_TO_TRACK.CLICK_UNSELECT_LOT_REPORT, {locationId: this.optLocations[index].id, lotName: this.optLocations[index].label, deviceId: this.equipData.id, deviceName: this.equipData.name})
      }
    },
    setMetricsData (firstRender = false) {
      const faultIndex = 1
      const faultsTimeIndex = 2
      // Initialization------------------
      const metricsResult = []
      for (let i = 0; i < 4; i++) {
        metricsResult.push({
          value: i === faultIndex ? {faults: 0, hours: 0} : 0,
          mod: {
            value: 0
          }
        })
      }
      let lengthCheckedOptions = 0
      const checkedArray = firstRender ? this.initOptLocations() : this.optLocations

      // Summation-----------------------
      this.locations.forEach((loc, i) => {
        if (checkedArray[i].checked) {
          loc.metrics.forEach((metric, i) => {
            if (i === faultIndex) {
              metricsResult[i].value.faults += loc.details.data.num_faults || 0
              metricsResult[i].value.hours += (loc.details.data.irrigation_hours && parseFloat(loc.details.data.irrigation_hours)) || 0
            } else {
              metricsResult[i].value += metric.value
            }
            metricsResult[i].mod.value += metric.mod.value
          })
          lengthCheckedOptions++
        }
      })
      // Average and last modifications-------------------------
      metricsResult.forEach((metric, i) => {
        if (i === faultIndex) {
          const {faults, hours} = metricsResult[i].value
          metricsResult[i].value = (faults && hours) ? this.roundFloat((faults / hours) * 100, 1) : 0
        } else {
          metricsResult[i].value /= lengthCheckedOptions
          metricsResult[i].value = this.roundFloat(metricsResult[i].value, 1)
        }
        metricsResult[i].mod.value /= lengthCheckedOptions
        metricsResult[i].mod.value = this.roundFloat(metricsResult[i].mod.value, 1)

        if (metricsResult[i].mod.value < 0) {
          metricsResult[i].mod.neg = true
          metricsResult[i].mod.value *= -1
        }

        if (i === faultsTimeIndex) {
          metricsResult[i].value = this.hoursToTime(metricsResult[i].value)
          metricsResult[i].mod.value = this.hoursToTime(metricsResult[i].mod.value)
        } else {
          metricsResult[i].mod.value += '%'
        }
      })

      return metricsResult
    },
    haveBadge (badge, value, index) {
      if (this.badgesInfo) {
        const isInverted = this.metricsVisualData[index].inverted ? -1 : 1
        if (badge === 'star') {
          return value * isInverted >= this.badgesInfo[index].star * isInverted
        }
        if (badge === 'alert') {
          return value * isInverted <= this.badgesInfo[index].alert * isInverted
        }
      }
    },
    isModGood (mod, index) {
      // If inverted, negative values are good, else, negative values aren't good
      return this.metricsVisualData[index].inverted ? mod.neg : !mod.neg
    },
    getMetricsVisualData () {
      return [
        {
          label: this.$t('MainCard.homogeneityCoefficient'),
          circular: true
        }, {
          label: this.$t('MainCard.faultsEvery100hs'),
          inverted: true // if true (star <=, alert >=) (good negatives values in mod)
        }, {
          label: this.$t('MainCard.responseTimeBeforeFaults'),
          inverted: true
        }, {
          label: this.$t('MainCard.irrigationOrderComplition'),
          circular: true
        }
      ]
    },
    roundFloat (val, n = 1) {
      const cant = 10 * n
      return Math.round((val + Number.EPSILON) * cant) / cant
    },
    hoursToTime (hours) {
      let auxHours = Math.floor(hours)

      let minutes = hours % 1
      minutes = Math.round(minutes * 30 / 0.5)
      if (minutes === 60) {
        minutes = 0
        auxHours++
      }

      let result = ''
      if (auxHours) result += `${auxHours}h`
      if (minutes) result += ` ${minutes}m`
      if (!result) result = '0h'

      return result
    },
    requestAuditQuote () {
      trackEvent(EVENTS_TO_TRACK.REQUEST_AUDIT_QUOTE, {deviceId: this.equipData.id, deviceName: this.equipData.name})
      window.open(`https://wa.me/${parsePhoneNumberForWaMe(process.env.VUE_APP_ESTEBAN_PHONE)}?text=${this.$t('MainCard.requestAuditQuoteMessage')}`, '_blank')
    }
  },
  computed: {
    equipmentComments () {
      const comments = []
      this.locations.forEach(loc => {
        if (loc.details.equipment_comment) {
          comments.push(loc.details.equipment_comment)
        }
      })
      return comments
    },
    faultsEvery100hs () {
      return parseFloat(this.metricsData[this.metricsVisualData.findIndex(metric => metric.label === this.$t('MainCard.faultsEvery100hs'))].value)
    }
  }
}
</script>


// WEBPACK FOOTER //
// MainCard.vue?83cd9214